import { CheckIcon } from 'lucide-react';
import { memo, useCallback } from 'react';

import { useTrackChangedVideoClosedCaptionTrack } from '@air/analytics';
import { ClosedCaptionsIcon } from '@air/next-icons';
import { DropdownMenu, DropdownMenuItem, DropdownMenuLabel } from '@air/primitive-dropdown-menu';
import { IconButton, type IconButtonProps } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';

import { usePlayerStore } from './PlayerContextProvider';

const PLAYER_SUBTITLES_BUTTON_TOOLTIP_NO_SUBTITLES_LABEL = 'No subtitles';

export type PlayerSubtitlesButtonProps = Omit<IconButtonProps, 'children' | 'label'>;

export const PlayerSubtitlesButton = memo((props: PlayerSubtitlesButtonProps) => {
  const id = usePlayerStore((state) => state.id);
  const hlsInstance = usePlayerStore((state) => state.hlsInstance);
  const subtitleTrack = usePlayerStore((state) => state.subtitleTrack);
  const subtitleTracks = usePlayerStore((state) => state.subtitleTracks);

  const isDefaultSubtitleTrack = subtitleTrack === -1;
  const hasSubtitles = !!subtitleTracks.length;
  const { trackChangedVideoClosedCaptionTrack } = useTrackChangedVideoClosedCaptionTrack();

  const handleTrackChange = useCallback(
    (trackIndex: number) => {
      if (hlsInstance) {
        hlsInstance.subtitleTrack = trackIndex;
      }

      if (trackIndex >= 0) {
        trackChangedVideoClosedCaptionTrack({
          id,
          track: subtitleTracks[trackIndex].name || subtitleTracks[trackIndex].lang || `Track ${trackIndex + 1}`,
        });
      }
    },
    [hlsInstance, id, trackChangedVideoClosedCaptionTrack, subtitleTracks],
  );

  const handleTrackOff = useCallback(() => {
    handleTrackChange(-1);
  }, [handleTrackChange]);

  if (!hasSubtitles) {
    return (
      <Tooltip label={PLAYER_SUBTITLES_BUTTON_TOOLTIP_NO_SUBTITLES_LABEL}>
        <IconButton
          appearance="ghost"
          color="grey"
          data-testid="PLAYER_SUBTITLES_BUTTON"
          disabled
          icon={ClosedCaptionsIcon}
          label={PLAYER_SUBTITLES_BUTTON_TOOLTIP_NO_SUBTITLES_LABEL}
          size="medium"
          {...props}
        />
      </Tooltip>
    );
  }

  return (
    <DropdownMenu
      side="top"
      trigger={
        <IconButton
          appearance="ghost"
          color={isDefaultSubtitleTrack ? 'grey' : 'blue'}
          data-testid="PLAYER_SUBTITLES_BUTTON"
          disabled={!hasSubtitles}
          icon={ClosedCaptionsIcon}
          label="Subtitles"
          size="medium"
          {...props}
        />
      }
    >
      <DropdownMenuLabel>Subtitles</DropdownMenuLabel>
      <DropdownMenuItem
        label="Off"
        onSelect={handleTrackOff}
        suffix={subtitleTrack === -1 ? <CheckIcon className="size-4" /> : null}
      />
      {subtitleTracks.map((track, index) => (
        <DropdownMenuItem
          key={track.id}
          label={track.name || track.lang || `Track ${index + 1}`}
          onSelect={() => handleTrackChange(index)}
          suffix={subtitleTrack === index ? <CheckIcon className="size-4" /> : null}
        />
      ))}
    </DropdownMenu>
  );
});

PlayerSubtitlesButton.displayName = 'PlayerSubtitlesButton';
